import React from "react";
import { ModeRendererProps } from "./types";

// VASARELY × GOYA: Moral complexity through structured tension
// Dark palettes, moral chiaroscuro, fractured grids
export function VasarelyGoyaMode({ brand }: ModeRendererProps) {
  return (
    <g>
      {/* Dark moral background */}
      <rect x="0" y="0" width="794" height="1123" fill={brand.black} opacity="0.9" />

      <g transform="translate(397 540)">
        {/* Fractured grid (broken systems) */}
        {Array.from({ length: 14 }).map((_, i) =>
          Array.from({ length: 18 }).map((_, j) => {
            const x = (i - 6.5) * 44;
            const y = (j - 8.5) * 40;
            const fracture = Math.sin(i * 0.7) * 15 + Math.cos(j * 0.6) * 12;
            const darkness = Math.random() > 0.5 ? 1 : 0.5;
            
            return (
              <rect
                key={`${i}-${j}`}
                x={x - 16 + fracture}
                y={y - 16 - fracture}
                width="32"
                height="32"
                fill={darkness > 0.7 ? brand.violet : brand.deep}
                stroke={brand.amber}
                strokeWidth="1"
                opacity={0.3 * darkness}
                transform={`rotate(${fracture} ${x} ${y})`}
              />
            );
          })
        )}

        {/* Moral tension lines (ethical complexity) */}
        {Array.from({ length: 16 }).map((_, i) => {
          const angle = (i * Math.PI) / 8;
          const distortion = Math.sin(i * 0.5) * 40;
          return (
            <line
              key={i}
              x1="0"
              y1="0"
              x2={Math.cos(angle) * (260 + distortion)}
              y2={Math.sin(angle) * (260 + distortion)}
              stroke={i % 3 === 0 ? brand.amber : brand.violet}
              strokeWidth="2"
              opacity="0.25"
              strokeDasharray="8 8"
            />
          );
        })}

        {/* Dark optical circles (witnessing eyes) */}
        {Array.from({ length: 12 }).map((_, i) => {
          const r = 50 + i * 22;
          const irregularity = Math.sin(i * 0.8) * 15;
          return (
            <ellipse
              key={i}
              cx={irregularity}
              cy={-irregularity * 0.5}
              rx={r}
              ry={r * (1 + Math.sin(i * 0.4) * 0.2)}
              fill="none"
              stroke={i % 2 === 0 ? brand.violet : brand.amber}
              strokeWidth="1.5"
              opacity={0.3 - i * 0.015}
            />
          );
        })}

        {/* Fractured squares (broken order) */}
        {[
          { x: -140, y: -160, size: 80, angle: 12 },
          { x: 120, y: -140, size: 70, angle: -8 },
          { x: -100, y: 120, size: 90, angle: -15 },
          { x: 140, y: 140, size: 75, angle: 10 },
        ].map((square, i) => (
          <g key={i}>
            <rect
              x={square.x - square.size / 2}
              y={square.y - square.size / 2}
              width={square.size}
              height={square.size}
              fill={brand.violet}
              opacity="0.15"
              transform={`rotate(${square.angle} ${square.x} ${square.y})`}
            />
            <rect
              x={square.x - square.size / 2}
              y={square.y - square.size / 2}
              width={square.size}
              height={square.size}
              fill="none"
              stroke={brand.amber}
              strokeWidth="3"
              opacity="0.4"
              transform={`rotate(${square.angle} ${square.x} ${square.y})`}
            />
          </g>
        ))}

        {/* Moral complexity nodes */}
        {[
          { x: 0, y: 0 },
          { x: -180, y: -120 },
          { x: 180, y: -100 },
          { x: -160, y: 140 },
          { x: 170, y: 130 },
        ].map((node, i) => (
          <g key={i}>
            <circle cx={node.x} cy={node.y} r="8" fill={brand.amber} opacity="0.5" />
            <circle cx={node.x} cy={node.y} r="14" fill="none" stroke={brand.amber} strokeWidth="2" opacity="0.3" />
            <circle cx={node.x} cy={node.y} r="20" fill="none" stroke={brand.violet} strokeWidth="1" opacity="0.2" />
          </g>
        ))}

        {/* Diagonal tension (el sueño de la razón) */}
        <line x1="-260" y1="-260" x2="260" y2="260" stroke={brand.violet} strokeWidth="3" opacity="0.3" strokeDasharray="12 8" />
        <line x1="260" y1="-260" x2="-260" y2="260" stroke={brand.amber} strokeWidth="3" opacity="0.3" strokeDasharray="12 8" />
      </g>

      {/* Moral inscription */}
      <text x="397" y="1040" textAnchor="middle" fontFamily="serif" fontSize="10" fill={brand.amber} opacity="0.5" fontStyle="italic">
        el sueño de la razón produce monstruos
      </text>
    </g>
  );
}
